import React, { useState, useRef } from "react";
import "./Home.css";
import { convertTimeToMilliseconds, determineMedal, getEventTimings } from "../../services/hotlapService";

export default function Home({ hotlapEvents, eventDetails }) {
  const [driversTable, setDriversTable] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("Select an event");
  const dropdownRef = useRef(null);

  const eventTimings = getEventTimings();

  const handleDropdownClick = () => {
    setIsOpen(!isOpen);
  };

  const handleEventSelection = (eventName, eventId) => {
    setSelectedEvent(eventName);
    setIsOpen(false);

    const eventXml = eventDetails[eventId];
    if (!eventXml) {
      console.error(`No event XML data found for ID: ${eventId}`);
      return;
    }

    const eventNameXml = eventXml.getElementsByTagName("name")[0]?.textContent || "";
    const sanitizedEventName = eventNameXml
      .replace(/OLR Challenge \d+: /, "")
      .trim()
      .toLowerCase();
    const availableKeys = Object.keys(eventTimings).map((key) => key.toLowerCase().trim());
    const matchingThresholdIndex = availableKeys.indexOf(sanitizedEventName);
    const matchingThresholds = matchingThresholdIndex !== -1 ? eventTimings[Object.keys(eventTimings)[matchingThresholdIndex]] : undefined;

    const subEvents = Array.from(eventXml.getElementsByTagName("sub_event"));
    const includeSplitPoints = eventNameXml.includes("OLR Challenge");

    let tableHTML = `
      <h3 class="event__name">${eventNameXml}</h3>
      <div class="scrollable-container">
        <table>
          <thead>
            <tr>
              <th>Position</th>
              <th>Driver</th>
              <th>Lap Time</th>
              <th>Date</th>
              <th>Telemetry</th>
              <th>Vehicle</th>
              ${includeSplitPoints ? "<th>Split</th><th>Points</th>" : ""}
            </tr>
          </thead>
          <tbody>
    `;

    subEvents.forEach((subEvent) => {
      const results = Array.from(subEvent.getElementsByTagName("result"));
      const includedDrivers = new Set();
      const uniqueResults = results.filter((result) => {
        const name = result.getElementsByTagName("customer_name")[0]?.textContent || "Unknown";
        if (includedDrivers.has(name)) return false;
        includedDrivers.add(name);
        return true;
      });

      uniqueResults.forEach((result, index) => {
        const customerId = result.getElementsByTagName("customer_id")[0]?.textContent;
        const name = result.getElementsByTagName("customer_name")[0]?.textContent || "Unknown";
        const lapTimeStr = result.getElementsByTagName("lap_time_str")[0]?.textContent || "-";
        const fullDate = result.getElementsByTagName("date")[0]?.textContent || "N/A";
        const [year, month, day] = fullDate.split(" ")[0].split("-");
        const formattedDate = `${month}/${day}/${year}`;
        const telemetryUri = result.getElementsByTagName("telemetry_uri")[0]?.textContent || "#";
        const vehicle = result.getElementsByTagName("vehicle_name")[0]?.textContent || "N/A";

        let splitInfo = `<td>-</td><td>0</td>`; // Default values: "-" for Split, "0" for Points

        if (includeSplitPoints && lapTimeStr && matchingThresholds) {
          const lapTimeMs = convertTimeToMilliseconds(lapTimeStr);
          const split = determineMedal(lapTimeMs, matchingThresholds);
          splitInfo = `<td class="${split.class}">${split.name}</td><td>${split.points || "0"}</td>`;
        }

        tableHTML += `
          <tr>
            <td>${index + 1}</td>
            <td><a href="/driver/${customerId}" class="driver-link">${name}</a></td>
            <td>${lapTimeStr}</td>
            <td>${formattedDate}</td>
            <td>
              <a href="${telemetryUri}" target="_blank" rel="noopener noreferrer">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="chart-line"
                  class="svg-inline--fa fa-chart-line fa-w-16"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="currentColor"
                    d="M496 384H64V80c0-8.84-7.16-16-16-16H16C7.16 64 0 71.16 0 80v336c0 17.67 14.33 32 32 32h464c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16zM464 96H345.94c-21.38 0-32.09 25.85-16.97 40.97l32.4 32.4L288 242.75l-73.37-73.37c-12.5-12.5-32.76-12.5-45.25 0l-68.69 68.69c-6.25 6.25-6.25 16.38 0 22.63l22.62 22.62c6.25 6.25 16.38 6.25 22.63 0L192 237.25l73.37 73.37c12.5 12.5 32.76 12.5 45.25 0l96-96 32.4 32.4c15.12 15.12 40.97 4.41 40.97-16.97V112c.01-8.84-7.15-16-15.99-16z"
                  ></path>
                </svg>
              </a>
            </td>
            <td>${vehicle}</td>
            ${includeSplitPoints ? splitInfo : ""}
          </tr>
        `;
      });
    });

    tableHTML += "</tbody></table></div>";
    setDriversTable(tableHTML);
  };

  return (
    <div>
      <br />
      <h2>Hotlapping Events</h2>
      <div className="dropdown-container" ref={dropdownRef}>
        <button className="dropdown-button" onClick={handleDropdownClick}>
          {selectedEvent}
          <span className="dropdown-arrow">&#9660;</span>
        </button>

        {isOpen && (
          <ul className="dropdown-menu">
            {hotlapEvents.map((event, index) => {
              const eventName = event.getElementsByTagName("name")[0]?.textContent || "Untitled Event";
              const eventId = event.getElementsByTagName("id")[0]?.textContent;
              return (
                <li key={index} className="dropdown-item" onClick={() => handleEventSelection(eventName, eventId)}>
                  {eventName}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div id="drivers-table" dangerouslySetInnerHTML={{ __html: driversTable }} />
    </div>
  );
}
